import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faTimes, faSyncAlt, faPercent, faQuestionCircle,
  faCalculator, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import abacusService from '../../services/abacusService';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import '../../components/ui/HMSTable.css';

const HELP_CONTENT = {
  title: 'Tax Codes',
  body: 'This tab displays all tax codes configured in your Abacus ERP instance, including the current rate and valid-from date for each code. Use the "Tax Rate Lookup" card to query the effective rate for a specific tax code on a given date. The table auto-loads on tab open and can be filtered using the search bar.',
};

const HelpModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="md-modal-overlay" onClick={onClose}>
      <div className="md-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="md-modal-header" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
          <div className="md-modal-header-text">
            <FontAwesomeIcon icon={faQuestionCircle} className="md-modal-header-icon" />
            <h2>{HELP_CONTENT.title}</h2>
          </div>
          <button className="md-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="md-modal-body">
          <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.7 }}>{HELP_CONTENT.body}</p>
        </div>
        <div className="md-modal-footer">
          <button className="md-btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AbacusTaxCodesTab = ({ books }) => {
  const [taxcodes, setTaxcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [lookupCode, setLookupCode] = useState('');
  const [lookupDate, setLookupDate] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await abacusService.getTaxcodes(books);
        setTaxcodes(data.taxcodes || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch tax codes from Abacus');
      } finally {
        setLoading(false);
      }
    };
    if (books) load();
  }, [books]);

  const getCurrentRate = (tc) => {
    if (!tc.TimeAxes || !tc.TimeAxes.length) return null;
    const sorted = [...tc.TimeAxes].sort((a, b) => (b.ValidFrom || '').localeCompare(a.ValidFrom || ''));
    return sorted[0];
  };

  const handleLookup = async () => {
    if (!lookupCode.trim()) return;
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const data = await abacusService.getTaxRate(books, lookupCode.trim(), lookupDate || null);
      setLookupResult(data);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to look up tax rate');
    } finally {
      setLookupLoading(false);
    }
  };

  const filtered = taxcodes.filter(tc => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return String(tc.Code || '').toLowerCase().includes(term);
  });

  return (
    <>
      <div className="abacus-lookup-card">
        <div className="abacus-lookup-card__header">
          <FontAwesomeIcon icon={faCalculator} style={{ color: '#0ea5e9', marginRight: 8 }} />
          <span>Tax Rate Lookup</span>
        </div>
        <div className="abacus-lookup-card__body">
          <div className="abacus-filter-group">
            <label className="abacus-filter-label">Tax Code</label>
            <input
              type="text"
              className="abacus-filter-input"
              placeholder="e.g. MwSt77"
              value={lookupCode}
              onChange={(e) => setLookupCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
              style={{ width: 150 }}
            />
          </div>
          <div className="abacus-filter-group">
            <label className="abacus-filter-label">As of Date (optional)</label>
            <InvoiceDatePicker
              value={lookupDate}
              onChange={(e) => setLookupDate(e.target.value)}
              name="lookupDate"
              placeholder="Select date"
              className="abacus-date-picker"
            />
          </div>
          <button className="abacus-action-btn abacus-action-btn--primary" onClick={handleLookup} disabled={lookupLoading || !lookupCode.trim()}>
            <FontAwesomeIcon icon={lookupLoading ? faSyncAlt : faCalculator} spin={lookupLoading} />
            Look Up
          </button>
          {lookupResult && (
            <div className="abacus-lookup-result">
              <span className="abacus-lookup-result__code">{lookupResult.code}</span>
              <span className="abacus-lookup-result__rate">{lookupResult.rate}%</span>
              <span className="abacus-lookup-result__date">as of {lookupResult.as_of}</span>
            </div>
          )}
          {lookupError && (
            <div className="abacus-lookup-result abacus-lookup-result--error">
              <FontAwesomeIcon icon={faExclamationCircle} />
              {lookupError}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="abacus-error-banner">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{error}</span>
        </div>
      )}

      <div className="invoice-list-container">
        <div className="abacus-card">
          <div className="abacus-section-header">
            <div className="abacus-section-header__left">
              <FontAwesomeIcon icon={faPercent} style={{ color: '#0ea5e9' }} />
              <span className="abacus-section-header__title">Abacus Tax Codes</span>
              {!loading && <span className="abacus-section-header__count">{filtered.length}</span>}
            </div>
            <div className="abacus-section-header__right">
              {taxcodes.length > 0 && (
                <>
                  <div className="master-data-search-box abacus-section-search">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      className="master-data-search-input"
                      placeholder="Search by tax code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button className="clear-search" onClick={() => setSearchTerm('')}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                  <span className="abacus-result-count">{filtered.length} of {taxcodes.length} codes</span>
                </>
              )}
            </div>
            <button className="abacus-section-header__help" title="About this tab" onClick={() => setHelpOpen(true)}>
              <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
          </div>

          {loading ? (
            <div className="abacus-card-loading">
              <HMSSkeleton lines={6} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state-container">
              <div className="empty-state-icon"><FontAwesomeIcon icon={faPercent} /></div>
              <div className="empty-state-text">
                <h3>{searchTerm ? 'No Matching Tax Codes' : 'No Tax Codes Found'}</h3>
                <p>{searchTerm ? 'Try a different search term' : 'No tax codes returned from Abacus'}</p>
              </div>
            </div>
          ) : (
            <div className="hms-table-wrap">
              <table className="hms-table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col" className="hms-table-num">Current Rate</th>
                    <th scope="col">Valid From</th>
                    <th scope="col" style={{ textAlign: 'center' }}>Periods</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tc) => {
                    const current = getCurrentRate(tc);
                    return (
                      <tr key={tc.Code}>
                        <td className="invoice-number-cell">{tc.Code}</td>
                        <td className="hms-table-num abacus-amount-cell">
                          {current ? `${current.Rate}%` : '-'}
                        </td>
                        <td className="abacus-date-cell">{current ? formatDate(current.ValidFrom) : '-'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <HMSBadge status="draft">{tc.TimeAxes?.length || 0}</HMSBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default AbacusTaxCodesTab;
